/**
 * @NOTE If you change something here, make sure that `src/utils/__mocks__/i18n.ts`
 * is accounted for!
 */
import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-chained-backend";
import ICU from "i18next-icu";
import en from "i18next-icu/locale-data/en";
import HttpBackend from "i18next-http-backend";
import { initReactI18next } from "react-i18next";
import * as ReactTimeago from "react-timeago";
import buildFormatter from "react-timeago/lib/formatters/buildFormatter";
import enStrings from "react-timeago/lib/language-strings/en";
import appEvents from "./appEvents";
import config from "../config";
import debug from "./debug";
import { get } from "./helpers";
import { getStoredApiToken } from "./api";
import { getStoredItem } from "../utils/localStorage";

export interface TimeAgoFormatters {
  [languageCode: string]: ReactTimeago.Formatter;
}

const DEFAULT_I18NEXT_OPTIONS: i18next.InitOptions = {
  lng: "en",
  fallbackLng: "en",
  ns: ["common", "countries", "nationalities"],
  load: "currentOnly",
  defaultNS: "common",
  keySeparator: false,
  debug: process.env.NODE_ENV === "development" && false,
  interpolation: {
    escapeValue: false
  },
  react: {
    wait: true,
    defaultTransParent: "span"
  },
  i18nFormat: {
    localeData: [en]
  },
  initImmediate: false
};

// Cache a list of the loaded i18next ICU locales
const loadedI18nLocales = ["en"];

const LANG_TO_ICU_LOCALE = new Map([["zh-Hans", "zh"], ["zh-Hant", "yue"]]);

// Cache loaded ReactTimeago.Formatter functions
export const timeAgoFormatters: TimeAgoFormatters = {
  en: buildFormatter(enStrings)
};

// Supported locales for react-timeago formatters
const TIMEAGO_FORMATTERS_SUPPORTED_LOCALES = [
  "af",
  "am",
  "ar",
  "az",
  "be",
  "bg",
  "bs",
  "ca",
  "cs",
  "cy",
  "da",
  "de",
  "dv",
  "el",
  "es",
  "et",
  "eu",
  "fa",
  "fi",
  "fr",
  "gl",
  "he",
  "hr",
  "hu",
  "hy",
  "id",
  "is",
  "it",
  "ja",
  "jv",
  "ko",
  "ky",
  "lt",
  "lv",
  "mk",
  "nl",
  "no",
  "pl",
  "pt",
  "pt-br",
  "ro",
  "rs",
  "ru",
  "rw",
  "si",
  "sk",
  "sl",
  "sq",
  "sr",
  "sv",
  "th",
  "tr",
  "uk",
  "ur",
  "uz",
  "vi",
  "zh-CN",
  "zh-TW"
];

const TIME_AGO_FORMATTER_MAP: { [languageCode: string]: string } = {
  "pt-BR": "pt-br",
  "zh-Hant": "zh-TW",
  "zh-Hans": "zh-CN"
};

export async function loadTimeAgoFormatter(
  language: string
): Promise<ReactTimeago.Formatter> {
  let languageCode: string = (language.indexOf("-") !== -1
    ? language.split("-")[0]
    : language
  ).toLowerCase();

  // Specific match for mapped language codes
  if (TIME_AGO_FORMATTER_MAP.hasOwnProperty(language)) {
    languageCode = TIME_AGO_FORMATTER_MAP[language];
  }
  // Check if matches a supported locale, if not then default to english
  else if (TIMEAGO_FORMATTERS_SUPPORTED_LOCALES.indexOf(languageCode) === -1) {
    debug(
      "[i18n] loadTimeAgoFormatter: languageCode not supported, defaulting to English",
      {
        language,
        languageCode
      }
    );

    return timeAgoFormatters.en;
  }

  // Use the existing loaded formatter
  if (
    timeAgoFormatters.hasOwnProperty(languageCode) &&
    timeAgoFormatters[languageCode]
  ) {
    debug("[i18n] loadTimeAgoFormatter: using cached formatter", {
      language,
      languageCode
    });

    return timeAgoFormatters[languageCode];
  }
  // Import and build the formatter
  else {
    debug("[i18n] loadTimeAgoFormatter: loading formatter", {
      language,
      languageCode
    });

    timeAgoFormatters[languageCode] = await import(
      /* webpackChunkName: "timeago" */
      `react-timeago/lib/language-strings/${languageCode}.js`
    ).then(langStrings => {
      return buildFormatter(langStrings.default);
    });

    return timeAgoFormatters[languageCode];
  }
}

/**
 * Load in any necessary locale data when the language changed.
 */
function changedI18nextLanguage(languageCode: string) {
  const localeCode = LANG_TO_ICU_LOCALE.has(languageCode)
    ? // Use specifically mapped language code
      String(LANG_TO_ICU_LOCALE.get(languageCode))
    : // Otherwise auto-detect
      (languageCode.indexOf("-") !== -1
        ? languageCode.split("-")[0]
        : languageCode
      ).toLowerCase();

  //debug("changedI18nextLanguage:", { languageCode, localeCode });

  if (localeCode !== "cimode" && loadedI18nLocales.indexOf(localeCode) === -1) {
    // Load the ICU locale data
    import(
      /* webpackChunkName: "locale" */
      `i18next-icu/locale-data/${localeCode}.js`
    ).then(localeData => {
      // Ensure it is marked so it doesn't load again
      loadedI18nLocales.push(localeCode);

      /*debug(`[i18n] Loaded locale data for ${localeCode}`, {
        localeData
      });*/
    });

    // Load the react-timeago locale data
    loadTimeAgoFormatter(languageCode);
  } else {
    //debug(`[i18n] Already loaded locale data for ${localeCode}`);
  }
}

function envI18nextConfigOverrides(env: string): any {
  const token = getStoredApiToken();
  const localPathOption = {
    loadPath: "/" + config.id + "/translations/{{lng}}/{{ns}}.json"
  };
  const backendOption = {
    loadPath:
      get(config, "services.api.baseUrl") +
      get(
        config,
        "services.api.translationsEndpoint",
        "/translations/{{lng}}/{{ns}}?app=ddc"
      ),
    crossDomain: true,
    customHeaders: {
      Authorization: "Bearer " + token //+ getStoredApiToken()
    },
    parse: function(data: any) {
      const dataObj = JSON.parse(data);
      return dataObj.data.result;
    }
  };
  const publicPathOption = {
    loadPath: get(config, "languages.loadPath")
  };

  const loadPath = get(config, "languages.loadPath");
  if (loadPath === "local") {
    return {
      backend: {
        backends: [HttpBackend],
        backendOptions: [localPathOption]
      }
    };
  }
  switch (env) {
    case "test":
      return {
        lng: "cimode",
        fallbackLng: false,
        debug: false,
        resources: {},
        backend: {
          backends: []
        },
        initImmediate: true
      };

    default:
      const firstOption =
        loadPath && loadPath.startsWith("http")
          ? publicPathOption
          : backendOption;
      return {
        backend: {
          backends: [HttpBackend, HttpBackend],
          backendOptions: [firstOption, localPathOption]
        }
      };
  }
}

/**
 * Create an additional i18next instance with the default options/events
 * @param options
 */
export function createI18nInstance(
  options?: i18next.InitOptions
): i18next.i18n {
  const initOptions = {
    ...DEFAULT_I18NEXT_OPTIONS,
    ...envI18nextConfigOverrides(process.env.NODE_ENV),
    ...options
  };
  const i18n = i18next.createInstance(initOptions);
  i18n.on("languageChanged", changedI18nextLanguage);
  return i18n;
}

// Trigger loading the i18next ICU locale data and react-timeago formatter when the language is changed
i18next.on("languageChanged", changedI18nextLanguage);

// removed bacuse was loading "en" again after user language already loaded
appEvents.on("app.initialised", () => {
  const initOptions = {
    ...DEFAULT_I18NEXT_OPTIONS,
    ...envI18nextConfigOverrides(process.env.NODE_ENV)
  };
  initOptions.lng = getStoredItem("language") || initOptions.lng;
  debug("[i18n] on app init", initOptions);
  i18next.use(Backend).init(initOptions);
});

// Default i18next instance

const language = getStoredItem("language", DEFAULT_I18NEXT_OPTIONS.lng);
i18next
  .use(LanguageDetector)
  .use(ICU)
  .use(Backend)
  .use(initReactI18next)
  .init({
    ...DEFAULT_I18NEXT_OPTIONS,
    ...envI18nextConfigOverrides(process.env.NODE_ENV),
    lng: language
  });

export default i18next;
